import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  AutoAwesomeRounded,
  FlagRounded,
  OpenInNewRounded,
  PaidRounded,
  AccountTreeRounded,
  SearchRounded,
} from '@mui/icons-material';
import { searchCompanies } from '../../api/companies';
import {
  approveDocumentCorporateTransactionCandidate,
  BulkProcessingRun,
  DocumentFindingsResponse,
  DocumentIntelligenceDocument,
  DocumentInventoryResponse,
  extractDocumentFindings,
  getBulkProcessingRun,
  getDocumentFindings,
  listDocuments,
  startBulkProcessing,
} from '../../api/documentIntelligence';
import { CompanyListItem } from '../../types/company';
import { useEnvironment } from '../../hooks/useEnvironment';
import { DocumentTimeline } from './DocumentTimeline';
import { CompanyStatePanel } from './CompanyStatePanel';
import { BatchProcessingPanel } from './BatchProcessingPanel';
import { PipelineFieldEvidence } from '../PipelineIntelligencePage/PipelineFieldEvidence';

const titleCase = (value: string): string =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const dateLabel = (value?: string | null): string => {
  if (!value) return 'No date';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const createIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (value) => {
    const random = Math.floor(Math.random() * 16);
    return (value === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
};

export const addShownDocumentKeys = (
  current: ReadonlySet<string>,
  shown: Iterable<string>
): { keys: Set<string>; truncated: boolean } => {
  const keys = new Set(current);
  for (const key of shown) keys.add(key);
  return { keys, truncated: false };
};

const documentKey = (doc: Pick<DocumentIntelligenceDocument, 'company_id' | 'content_id'>) =>
  `${doc.company_id}:${doc.content_id}`;

const bulkRunFinished = (run: BulkProcessingRun): boolean =>
  ['completed', 'partial', 'failed'].includes(run.status);

const requestDetail = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  const detail = response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    if (response?.status === 404 && /not found/i.test(detail)) {
      return (
        'Document Intelligence API was not found on this backend (HTTP 404). ' +
        'The frontend is updated, but the backend likely still needs redeploy/restart ' +
        'with the feat/multi-extractor-alignment routes. ' +
        'If the backend is already updated, confirm the environment toggle and retry.'
      );
    }
    return detail;
  }
  if (response?.status === 404) {
    return (
      'Document Intelligence API route missing (HTTP 404). Redeploy/restart the backend ' +
      'so /api/v1/document-intelligence is available.'
    );
  }
  return fallback;
};

const DocumentIntelligencePage: React.FC = () => {
  const { environment } = useEnvironment();
  const previousEnvironment = useRef(environment);
  const inventoryRequestGeneration = useRef(0);
  const loadMoreRequestGeneration = useRef(0);
  const loadMoreInFlight = useRef(false);
  const findingsRequestGeneration = useRef(0);
  const extractRequestGeneration = useRef(0);
  const corporateTransactionSaveGeneration = useRef(0);
  const corporateTransactionSaveInFlight = useRef(false);
  const bulkPollGeneration = useRef(0);
  const bulkIdempotencyKey = useRef<string | null>(null);
  const [companyOptions, setCompanyOptions] = useState<CompanyListItem[]>([]);
  const [companyQuery, setCompanyQuery] = useState('');
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyListItem | null>(null);
  const [daysBack, setDaysBack] = useState(30);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingMoreDocs, setLoadingMoreDocs] = useState(false);
  const [inventory, setInventory] = useState<DocumentInventoryResponse | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<DocumentIntelligenceDocument | null>(null);
  const [findings, setFindings] = useState<DocumentFindingsResponse | null>(null);
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [savingCorporateTransaction, setSavingCorporateTransaction] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [pageTab, setPageTab] = useState(0);
  const [selectedDocumentKeys, setSelectedDocumentKeys] = useState<Set<string>>(() => new Set());
  const [bulkAnalyses, setBulkAnalyses] = useState({
    milestones: true,
    pipeline_intelligence: true,
    corporate_transactions: true,
  });
  const [confirmCorporateTransactionWrites, setConfirmCorporateTransactionWrites] = useState(false);
  const [bulkRun, setBulkRun] = useState<BulkProcessingRun | null>(null);
  const [startingBulkRun, setStartingBulkRun] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'press_release' | 'presentation'>('all');
  const [candidateStates, setCandidateStates] = useState<
    Record<string, { status: 'saving' | 'saved' | 'error'; message?: string }>
  >({});
  useEffect(
    () => () => {
      bulkPollGeneration.current += 1;
    },
    []
  );

  useEffect(() => {
    if (!companyQuery || companyQuery.trim().length < 2) {
      setCompanyOptions([]);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setSearchingCompanies(true);
      try {
        const result = await searchCompanies(companyQuery.trim(), 20);
        if (!cancelled) setCompanyOptions(result.results || []);
      } catch {
        if (!cancelled) setCompanyOptions([]);
      } finally {
        if (!cancelled) setSearchingCompanies(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [companyQuery]);

  const resolveCompany = async (): Promise<CompanyListItem | null> => {
    if (selectedCompany?.id) return selectedCompany;
    const term = companyQuery.trim();
    if (term.length < 2) return null;
    try {
      const result = await searchCompanies(term, 20);
      const results = result.results || [];
      const exact = results.find(
        (c) =>
          c.name.toLowerCase() === term.toLowerCase() || c.id.toLowerCase() === term.toLowerCase()
      );
      const match = exact || (results.length === 1 ? results[0] : null);
      if (match) {
        setSelectedCompany(match);
        setCompanyOptions(results);
        return match;
      }
      if (results.length > 1) {
        setCompanyOptions(results);
        setError(
          `Multiple companies match "${term}". Pick one from the dropdown, then load documents.`
        );
        return null;
      }
      setError(`No company found for "${term}". Try another spelling or ticker.`);
      return null;
    } catch {
      setError('Company search failed. Check your connection and try again.');
      return null;
    }
  };

  const loadDocuments = async (companyOverride?: CompanyListItem | null) => {
    const requestGeneration = ++inventoryRequestGeneration.current;
    bulkPollGeneration.current += 1;
    bulkIdempotencyKey.current = null;
    setSelectedDocumentKeys(new Set());
    setBulkRun(null);
    setStartingBulkRun(false);
    setConfirmCorporateTransactionWrites(false);
    loadMoreRequestGeneration.current += 1;
    findingsRequestGeneration.current += 1;
    extractRequestGeneration.current += 1;
    corporateTransactionSaveGeneration.current += 1;
    corporateTransactionSaveInFlight.current = false;
    loadMoreInFlight.current = false;
    setLoadingMoreDocs(false);
    setExtracting(false);
    setSavingCorporateTransaction(false);
    const hasOverride = companyOverride !== undefined;
    let company = hasOverride ? companyOverride : selectedCompany;
    if (!company && !hasOverride && companyQuery.trim()) {
      company = await resolveCompany();
      if (requestGeneration !== inventoryRequestGeneration.current) return;
      if (!company) {
        setLoadingDocs(false);
        return;
      }
    }
    if (!Number.isInteger(daysBack) || daysBack < 1) {
      setError('Lookback must be a positive whole number of days.');
      setLoadingDocs(false);
      return;
    }
    setLoadingDocs(true);
    setError(null);
    setSelectedDoc(null);
    setFindings(null);
    try {
      const inv = await listDocuments(company?.id ?? null, daysBack);
      if (requestGeneration !== inventoryRequestGeneration.current) return;
      setInventory(inv);
      if (inv.documents_found === 0) {
        setError(
          `No press releases or presentations for ${inv.company_name || company?.name || 'any company'} in the last ${daysBack} days. ` +
            'Try a longer lookback or confirm the selected environment.'
        );
      }
    } catch (err) {
      if (requestGeneration !== inventoryRequestGeneration.current) return;
      setInventory(null);
      setError(requestDetail(err, 'Could not load recent press releases and presentations.'));
    } finally {
      if (requestGeneration === inventoryRequestGeneration.current) {
        setLoadingDocs(false);
      }
    }
  };

  useEffect(() => {
    void loadDocuments(null);
    // Load the default all-company view once on entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMoreDocuments = async () => {
    if (
      !inventory?.has_more ||
      loadingDocs ||
      loadMoreInFlight.current ||
      inventory.company_id ||
      !inventory.next_cursor_date ||
      !inventory.next_cursor_title ||
      !inventory.next_cursor_id ||
      !inventory.next_cursor_company_id
    )
      return;
    const requestGeneration = ++inventoryRequestGeneration.current;
    const loadMoreGeneration = ++loadMoreRequestGeneration.current;
    const currentInventory = inventory;
    loadMoreInFlight.current = true;
    setLoadingMoreDocs(true);
    setError(null);
    try {
      const page = await listDocuments(null, currentInventory.days_back, {
        limit: 500,
        afterDate: currentInventory.next_cursor_date!,
        afterTitle: currentInventory.next_cursor_title!,
        afterId: currentInventory.next_cursor_id!,
        afterCompanyId: currentInventory.next_cursor_company_id!,
        reviewAsOf: currentInventory.review_as_of,
      });
      if (
        requestGeneration !== inventoryRequestGeneration.current ||
        loadMoreGeneration !== loadMoreRequestGeneration.current
      )
        return;
      const existing = new Set(
        currentInventory.documents.map((doc) => `${doc.company_id}:${doc.content_id}`)
      );
      const additional = page.documents.filter(
        (doc) => !existing.has(`${doc.company_id}:${doc.content_id}`)
      );
      setInventory({
        ...page,
        documents: [...currentInventory.documents, ...additional],
        documents_found: currentInventory.documents_found + additional.length,
        press_releases_found:
          currentInventory.press_releases_found +
          additional.filter((doc) => doc.source_type === 'press_release').length,
        presentations_found:
          currentInventory.presentations_found +
          additional.filter((doc) => doc.source_type === 'presentation').length,
      });
    } catch (err) {
      if (
        requestGeneration === inventoryRequestGeneration.current &&
        loadMoreGeneration === loadMoreRequestGeneration.current
      ) {
        setError(requestDetail(err, 'Could not load older documents.'));
      }
    } finally {
      if (loadMoreGeneration === loadMoreRequestGeneration.current) {
        loadMoreInFlight.current = false;
        setLoadingMoreDocs(false);
      }
    }
  };

  useEffect(() => {
    if (previousEnvironment.current === environment) return;
    previousEnvironment.current = environment;
    setInventory(null);
    setSelectedDoc(null);
    setFindings(null);
    setError(null);
    setSuccess(null);
    setCandidateStates({});
    setSourceFilter('all');
    void loadDocuments(selectedCompany);
    // Reload the active company filter (or all companies) against the new database.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment]);

  const onCompanyChange = async (company: CompanyListItem | null) => {
    setSelectedCompany(company);
    setInventory(null);
    setSelectedDoc(null);
    setFindings(null);
    setError(null);
    setSuccess(null);
    await loadDocuments(company);
  };

  const loadFindings = async (doc: DocumentIntelligenceDocument) => {
    const requestGeneration = ++findingsRequestGeneration.current;
    setSelectedDoc(doc);
    setLoadingFindings(true);
    setError(null);
    setSuccess(null);
    setFindings(null);
    setCandidateStates({});
    setTab(0);
    try {
      const response = await getDocumentFindings(doc.company_id, doc.content_id, doc.source_type, {
        title: doc.title,
        url: doc.url,
      });
      if (requestGeneration === findingsRequestGeneration.current) {
        setFindings(response);
      }
    } catch (err) {
      if (requestGeneration === findingsRequestGeneration.current) {
        setError(requestDetail(err, 'Could not load extractor findings for this document.'));
      }
    } finally {
      if (requestGeneration === findingsRequestGeneration.current) {
        setLoadingFindings(false);
      }
    }
  };

  const openDocumentReview = (doc: DocumentIntelligenceDocument) => {
    setPageTab(1);
    void loadFindings(doc);
  };

  const refreshTimeline = async (expectedFindingsGeneration: number) => {
    if (expectedFindingsGeneration !== findingsRequestGeneration.current) return;
    const requestGeneration = ++inventoryRequestGeneration.current;
    loadMoreRequestGeneration.current += 1;
    loadMoreInFlight.current = false;
    setLoadingMoreDocs(false);
    try {
      const refreshed = await listDocuments(
        selectedCompany?.id ?? null,
        inventory?.days_back ?? daysBack
      );
      if (
        requestGeneration === inventoryRequestGeneration.current &&
        expectedFindingsGeneration === findingsRequestGeneration.current
      ) {
        setInventory(refreshed);
      }
    } catch {
      if (
        requestGeneration === inventoryRequestGeneration.current &&
        expectedFindingsGeneration === findingsRequestGeneration.current
      ) {
        setError(
          'Findings were updated, but the content timeline could not refresh. Reload documents to retry.'
        );
      }
    }
  };

  const runExtract = async () => {
    if (!selectedDoc || corporateTransactionSaveInFlight.current) return;
    const document = selectedDoc;
    const findingsGeneration = ++findingsRequestGeneration.current;
    const extractGeneration = ++extractRequestGeneration.current;
    setExtracting(true);
    setError(null);
    setCandidateStates({});
    try {
      const updatedFindings = await extractDocumentFindings(
        document.company_id,
        document.content_id,
        document.source_type
      );
      if (findingsGeneration !== findingsRequestGeneration.current) return;
      setFindings(updatedFindings);
      await refreshTimeline(findingsGeneration);
    } catch (err) {
      if (findingsGeneration === findingsRequestGeneration.current) {
        setError(requestDetail(err, 'Live extract failed for this document.'));
      }
    } finally {
      if (extractGeneration === extractRequestGeneration.current) {
        setExtracting(false);
      }
    }
  };

  const saveCorporateTransactionCandidate = async (candidateId: string) => {
    if (!selectedDoc || !findings || extracting || corporateTransactionSaveInFlight.current) return;
    const document = selectedDoc;
    const finding = findings.corporate_transactions.find((item) => item.candidate_id === candidateId);
    if (!finding?.candidate) return;
    const findingsGeneration = ++findingsRequestGeneration.current;
    const saveGeneration = ++corporateTransactionSaveGeneration.current;
    corporateTransactionSaveInFlight.current = true;
    setSavingCorporateTransaction(true);
    setCandidateStates((current) => ({
      ...current,
      [candidateId]: { status: 'saving' },
    }));
    try {
      const persisted = await approveDocumentCorporateTransactionCandidate(
        document.company_id,
        document.source_type,
        finding.candidate
      );
      if (findingsGeneration !== findingsRequestGeneration.current) return;
      const updatedFindings = await getDocumentFindings(
        document.company_id,
        document.content_id,
        document.source_type
      );
      if (findingsGeneration !== findingsRequestGeneration.current) return;
      setFindings(updatedFindings);
      await refreshTimeline(findingsGeneration);
      if (findingsGeneration !== findingsRequestGeneration.current) return;
      setCandidateStates((current) => ({
        ...current,
        [candidateId]: {
          status: 'saved',
          message: 'Saved to Neo4j via Pantograph.',
        },
      }));
      setSuccess(
        `Saved corporate transaction ${persisted.transaction_id} to Neo4j via Pantograph.`
      );
    } catch (err) {
      if (findingsGeneration !== findingsRequestGeneration.current) return;
      setCandidateStates((current) => ({
        ...current,
        [candidateId]: {
          status: 'error',
          message: requestDetail(err, 'This corporate transaction could not be saved.'),
        },
      }));
    } finally {
      if (saveGeneration === corporateTransactionSaveGeneration.current) {
        corporateTransactionSaveInFlight.current = false;
        setSavingCorporateTransaction(false);
      }
    }
  };

  const filteredDocs = useMemo(() => {
    const docs = inventory?.documents || [];
    if (sourceFilter === 'all') return docs;
    return docs.filter((d) => d.source_type === sourceFilter);
  }, [inventory, sourceFilter]);

  const shownKeys = useMemo(() => new Set(filteredDocs.map(documentKey)), [filteredDocs]);
  const selectedShownCount = useMemo(
    () => [...shownKeys].filter((key) => selectedDocumentKeys.has(key)).length,
    [shownKeys, selectedDocumentKeys]
  );
  const allShownSelected = filteredDocs.length > 0 && selectedShownCount === filteredDocs.length;

  const toggleDocumentSelection = (doc: DocumentIntelligenceDocument) => {
    const key = documentKey(doc);
    setSelectedDocumentKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllShown = () => {
    if (allShownSelected) {
      setSelectedDocumentKeys((current) => {
        const next = new Set(current);
        shownKeys.forEach((key) => next.delete(key));
        return next;
      });
      return;
    }
    const result = addShownDocumentKeys(selectedDocumentKeys, shownKeys);
    setSelectedDocumentKeys(result.keys);
  };

  const reportBulkResult = (run: BulkProcessingRun) => {
    bulkIdempotencyKey.current = null;
    const failed = run.outcomes.filter((item) => item.status !== 'completed').length;
    setSuccess(
      `${run.documents_completed} of ${run.documents_total} documents processed` +
        (failed ? ` · ${failed} with errors` : '')
    );
  };

  const pollBulkRun = (runId: string, generation: number, attempt = 0) => {
    const delayMs = Math.min(750 * 2 ** attempt, 15_000);
    window.setTimeout(() => {
      void getBulkProcessingRun(runId)
        .then((next) => {
          if (generation !== bulkPollGeneration.current) return;
          setBulkRun(next);
          if (bulkRunFinished(next)) reportBulkResult(next);
          else pollBulkRun(runId, generation, 0);
        })
        .catch((err) => {
          if (generation === bulkPollGeneration.current) {
            setError(
              requestDetail(
                err,
                'Could not refresh bulk processing progress; retrying automatically.'
              )
            );
            pollBulkRun(runId, generation, attempt + 1);
          }
        });
    }, delayMs);
  };

  const runSelectedDocuments = async () => {
    if (!inventory || startingBulkRun || (bulkRun && !bulkRunFinished(bulkRun))) return;
    const documents = inventory.documents.filter((doc) =>
      selectedDocumentKeys.has(documentKey(doc))
    );
    const analyses: Array<'milestones' | 'pipeline_intelligence' | 'corporate_transactions'> = [];
    if (bulkAnalyses.milestones) analyses.push('milestones');
    if (bulkAnalyses.pipeline_intelligence) analyses.push('pipeline_intelligence');
    if (bulkAnalyses.corporate_transactions) analyses.push('corporate_transactions');
    if (documents.length === 0 || analyses.length === 0) return;
    if (bulkAnalyses.corporate_transactions && !confirmCorporateTransactionWrites) {
      setError('Confirm canonical Corporate Transaction writes before starting this run.');
      return;
    }
    const generation = ++bulkPollGeneration.current;
    const idempotencyKey = bulkIdempotencyKey.current || createIdempotencyKey();
    bulkIdempotencyKey.current = idempotencyKey;
    setStartingBulkRun(true);
    setBulkRun(null);
    setError(null);
    setSuccess(null);
    try {
      const run = await startBulkProcessing(
        {
          documents: documents.map((doc) => ({
            company_id: doc.company_id,
            content_id: doc.content_id,
            source_type: doc.source_type,
            title: doc.title,
          })),
          analyses,
          confirm_corporate_transaction_canonical_write:
            bulkAnalyses.corporate_transactions && confirmCorporateTransactionWrites,
        },
        idempotencyKey
      );
      if (generation !== bulkPollGeneration.current) return;
      setBulkRun(run);
      if (bulkRunFinished(run)) reportBulkResult(run);
      else pollBulkRun(run.run_id, generation);
    } catch (err) {
      if (generation === bulkPollGeneration.current) {
        setError(requestDetail(err, 'Could not start bulk document processing.'));
      }
    } finally {
      if (generation === bulkPollGeneration.current) setStartingBulkRun(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Document Intelligence
          </Typography>
          <Typography color="text.secondary">
            Review recent press releases and presentations across all companies, or filter by
            company, then inspect milestones, pipeline claims, and corporate transactions found for
            a selected document.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'flex-end' }}
          >
            <Autocomplete
              sx={{ flex: 1, minWidth: 280 }}
              options={companyOptions}
              loading={searchingCompanies}
              value={selectedCompany}
              onChange={(_e, value) => {
                void onCompanyChange(value);
              }}
              onInputChange={(_e, value) => setCompanyQuery(value)}
              getOptionLabel={(option) => option.name || option.id}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText={
                companyQuery.trim().length < 2
                  ? 'Type at least 2 characters'
                  : searchingCompanies
                    ? 'Searching…'
                    : 'No companies found'
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company (optional)"
                  placeholder="All companies"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchRounded fontSize="small" color="action" sx={{ mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              label="Days back"
              type="number"
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
              inputProps={{ min: 1 }}
              sx={{ width: 140 }}
            />
            <Button
              variant="contained"
              onClick={() => {
                void loadDocuments();
              }}
              disabled={loadingDocs}
              startIcon={loadingDocs ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {loadingDocs ? 'Loading…' : 'Load documents'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Leave Company blank to review all companies from latest to earliest. Select a company to
            filter the list. Use the environment toggle to load documents from the selected
            database.
          </Typography>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Tabs value={pageTab} onChange={(_e, value) => setPageTab(value)}>
          <Tab label="Content timeline" />
          <Tab label="Document review" />
          <Tab label="Company State" />
          <Tab label="Batch processing" />
        </Tabs>

        {pageTab === 3 && (
          <BatchProcessingPanel daysBack={daysBack} environment={environment} />
        )}

        {pageTab === 2 && (
          <CompanyStatePanel
            company={selectedCompany}
            environment={environment}
            refreshToken={
              bulkRun && bulkRunFinished(bulkRun) ? `${bulkRun.run_id}:${bulkRun.status}` : null
            }
          />
        )}

        {inventory && pageTab === 0 && (
          <DocumentTimeline documents={filteredDocs} onOpenDocument={openDocumentReview} />
        )}

        {inventory && pageTab === 1 && (
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
            <Paper variant="outlined" sx={{ flex: 1, minWidth: 320, maxWidth: { lg: 420 }, p: 2 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {inventory.company_name || selectedCompany?.name || 'All companies'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {inventory.documents_found} documents loaded from last {inventory.days_back}{' '}
                    days · {inventory.press_releases_found} PR · {inventory.presentations_found}{' '}
                    presentations
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {(
                    [
                      ['all', 'All'],
                      ['press_release', 'Press releases'],
                      ['presentation', 'Presentations'],
                    ] as const
                  ).map(([value, label]) => (
                    <Chip
                      key={value}
                      label={label}
                      color={sourceFilter === value ? 'primary' : 'default'}
                      variant={sourceFilter === value ? 'filled' : 'outlined'}
                      onClick={() => setSourceFilter(value)}
                      size="small"
                    />
                  ))}
                </Stack>

                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Run and save selected documents
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allShownSelected}
                          indeterminate={
                            selectedShownCount > 0 && selectedShownCount < filteredDocs.length
                          }
                          onChange={toggleAllShown}
                          inputProps={{
                            'aria-label': 'Select all shown documents',
                          }}
                        />
                      }
                      label={`Select all shown (${filteredDocs.length})`}
                    />
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={bulkAnalyses.milestones}
                            onChange={(event) =>
                              setBulkAnalyses((current) => ({
                                ...current,
                                milestones: event.target.checked,
                              }))
                            }
                          />
                        }
                        label="Milestones"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={bulkAnalyses.pipeline_intelligence}
                            onChange={(event) =>
                              setBulkAnalyses((current) => ({
                                ...current,
                                pipeline_intelligence: event.target.checked,
                              }))
                            }
                          />
                        }
                        label="Pipeline"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={bulkAnalyses.corporate_transactions}
                            onChange={(event) => {
                              setBulkAnalyses((current) => ({
                                ...current,
                                corporate_transactions: event.target.checked,
                              }));
                              if (!event.target.checked) setConfirmCorporateTransactionWrites(false);
                            }}
                          />
                        }
                        label="Corporate Transactions"
                      />
                    </FormGroup>
                    {bulkAnalyses.corporate_transactions && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={confirmCorporateTransactionWrites}
                            onChange={(event) => setConfirmCorporateTransactionWrites(event.target.checked)}
                            inputProps={{
                              'aria-label': 'Confirm canonical Corporate Transaction writes',
                            }}
                          />
                        }
                        label="Save extracted Corporate Transactions as canonical transactions"
                      />
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => void runSelectedDocuments()}
                      disabled={
                        startingBulkRun ||
                        Boolean(bulkRun && !bulkRunFinished(bulkRun)) ||
                        selectedDocumentKeys.size === 0 ||
                        !Object.values(bulkAnalyses).some(Boolean) ||
                        (bulkAnalyses.corporate_transactions && !confirmCorporateTransactionWrites)
                      }
                      startIcon={
                        startingBulkRun ? <CircularProgress size={15} color="inherit" /> : undefined
                      }
                    >
                      {startingBulkRun ? 'Starting…' : 'Run and save selected'}
                    </Button>
                    {bulkRun && (
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={
                            bulkRun.documents_total
                              ? (bulkRun.documents_completed / bulkRun.documents_total) * 100
                              : 0
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          {bulkRun.documents_completed} of {bulkRun.documents_total} documents
                          processed · {titleCase(bulkRun.status)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
                {inventory.truncated_sources.length > 0 && (
                  <Alert severity="warning">
                    Inventory truncated for: {inventory.truncated_sources.join(', ')}
                  </Alert>
                )}
                <Divider />
                {filteredDocs.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No documents in this window.
                  </Typography>
                ) : (
                  <List dense disablePadding sx={{ maxHeight: 560, overflow: 'auto' }}>
                    {filteredDocs.map((doc) => {
                      const selected =
                        selectedDoc?.content_id === doc.content_id &&
                        selectedDoc?.company_id === doc.company_id;
                      const checked = selectedDocumentKeys.has(documentKey(doc));
                      return (
                        <ListItemButton
                          key={`${doc.company_id}:${doc.source_type}:${doc.content_id}`}
                          selected={selected}
                          onClick={() => loadFindings(doc)}
                          alignItems="flex-start"
                          sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                          <Checkbox
                            checked={checked}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => toggleDocumentSelection(doc)}
                            inputProps={{ 'aria-label': `Select ${doc.title}` }}
                            sx={{ mt: -0.5, ml: -1 }}
                          />
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  size="small"
                                  label={doc.source_type === 'press_release' ? 'PR' : 'Deck'}
                                  color={doc.source_type === 'press_release' ? 'info' : 'secondary'}
                                  variant="outlined"
                                />
                                <Typography variant="body2" fontWeight={600}>
                                  {doc.title}
                                </Typography>
                              </Stack>
                            }
                            secondary={
                              <Box component="span">
                                <Typography variant="caption" color="text.secondary">
                                  {doc.company_name || doc.company_id} ·{' '}
                                  {dateLabel(doc.published_date)} · CTX{' '}
                                  {doc.last_corporate_transaction_pipeline_run_status || 'not run'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ flex: 2, p: 2.5, minHeight: 480 }}>
              {!selectedDoc && (
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 360,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    Select a press release or presentation to inspect findings.
                  </Typography>
                </Box>
              )}

              {selectedDoc && (
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedDoc.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedDoc.company_name || selectedDoc.company_id} ·{' '}
                        {titleCase(selectedDoc.source_type)} ·{' '}
                        {dateLabel(selectedDoc.published_date)}
                      </Typography>
                      {selectedDoc.url && (
                        <Link
                          href={selectedDoc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          Open source <OpenInNewRounded fontSize="inherit" />
                        </Link>
                      )}
                    </Box>
                    <Stack spacing={0.5} alignItems={{ sm: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        startIcon={
                          extracting ? <CircularProgress size={16} /> : <AutoAwesomeRounded />
                        }
                        onClick={runExtract}
                        disabled={extracting || loadingFindings || savingCorporateTransaction}
                      >
                        {extracting ? 'Extracting…' : 'Run live extract'}
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Uses the models selected in LLM / Model Configuration.
                      </Typography>
                    </Stack>
                  </Stack>

                  {loadingFindings && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress />
                    </Box>
                  )}

                  {findings && !loadingFindings && (
                    <>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {findings.sections.map((section) => (
                          <Chip
                            key={section.extractor}
                            icon={
                              section.extractor === 'milestones' ? (
                                <FlagRounded />
                              ) : section.extractor === 'pipeline_intelligence' ? (
                                <AccountTreeRounded />
                              ) : (
                                <PaidRounded />
                              )
                            }
                            label={`${titleCase(section.extractor)}: ${section.count} (${section.status})`}
                            color={
                              section.status === 'error'
                                ? 'error'
                                : section.status === 'empty'
                                  ? 'default'
                                  : 'primary'
                            }
                            variant="outlined"
                          />
                        ))}
                      </Stack>

                      {findings.sections
                        .filter((s) => s.message)
                        .map((s) => (
                          <Alert
                            key={`${s.extractor}-msg`}
                            severity={s.status === 'error' ? 'error' : 'info'}
                          >
                            <strong>{titleCase(s.extractor)}:</strong> {s.message}
                          </Alert>
                        ))}

                      <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
                        <Tab
                          icon={<FlagRounded />}
                          iconPosition="start"
                          label={`Milestones (${findings.milestones.length})`}
                        />
                        <Tab
                          icon={<AccountTreeRounded />}
                          iconPosition="start"
                          label={`Pipeline (${findings.pipeline.length})`}
                        />
                        <Tab
                          icon={<PaidRounded />}
                          iconPosition="start"
                          label={`Corporate Transactions (${findings.corporate_transactions.length})`}
                        />
                      </Tabs>

                      {tab === 0 && (
                        <Stack spacing={1.25}>
                          {findings.milestones.length === 0 ? (
                            <Typography color="text.secondary">
                              No milestones for this document. Try Run live extract.
                            </Typography>
                          ) : (
                            findings.milestones.map((m) => (
                              <Paper key={m.id || m.milestone} variant="outlined" sx={{ p: 1.5 }}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  mb={0.5}
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  {m.origin === 'live_extract' && (
                                    <Chip size="small" label="Live" color="warning" />
                                  )}
                                  <Chip size="small" label={m.status} />
                                  <Typography variant="caption" color="text.secondary">
                                    {m.date_label}
                                    {m.date_start ? ` · ${m.date_start}` : ''}
                                  </Typography>
                                </Stack>
                                <Typography>{m.milestone}</Typography>
                              </Paper>
                            ))
                          )}
                        </Stack>
                      )}

                      {tab === 1 && (
                        <Stack spacing={1.25}>
                          {findings.pipeline.length === 0 ? (
                            <Typography color="text.secondary">
                              No pipeline suggestions cite this document.
                            </Typography>
                          ) : (
                            findings.pipeline.map((p) => (
                              <Paper key={p.key} variant="outlined" sx={{ p: 1.5 }}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  flexWrap="wrap"
                                  useFlexGap
                                  mb={0.75}
                                >
                                  <Chip
                                    size="small"
                                    label={titleCase(p.difference_type)}
                                    color="primary"
                                    variant="outlined"
                                  />
                                  <Chip size="small" label={p.status} />
                                  {p.addable && (
                                    <Chip size="small" label="Addable" color="success" />
                                  )}
                                </Stack>
                                <Typography fontWeight={600}>{p.therapeutic_name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {[p.reported_stage || p.normalized_stage, p.indication_text]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                  <PipelineFieldEvidence
                                    biologicalTargetStated={p.biological_target_stated}
                                    evidenceByField={p.evidence_by_field || {}}
                                    fields={[
                                      {
                                        key: 'therapeutic_name',
                                        label: 'Therapeutic',
                                        value: p.therapeutic_name,
                                      },
                                      {
                                        key: 'reported_stage',
                                        label: 'Stage as stated',
                                        value: p.reported_stage,
                                      },
                                      {
                                        key: 'normalized_stage',
                                        label: 'Normalized stage',
                                        value: p.normalized_stage,
                                      },
                                      {
                                        key: 'indication_text',
                                        label: 'Indication',
                                        value: p.indication_text,
                                      },
                                      {
                                        key: 'modality',
                                        label: 'Modality',
                                        value: p.modality,
                                      },
                                      {
                                        key: 'mechanism_of_action',
                                        label: 'Mechanism of action',
                                        value: p.mechanism_of_action,
                                      },
                                      {
                                        key: 'biological_targets',
                                        label: 'Biological target',
                                        value: p.targets,
                                      },
                                      {
                                        key: 'owner_name',
                                        label: 'Owner / primary developer',
                                        value: p.owner_name,
                                      },
                                      {
                                        key: 'development_model',
                                        label: 'Development model',
                                        value: p.development_model,
                                      },
                                      {
                                        key: 'partner_names',
                                        label: 'Partners',
                                        value: p.partner_names,
                                      },
                                      {
                                        key: 'substantive_facts',
                                        label: 'Other source-stated facts',
                                        value: p.substantive_facts,
                                      },
                                    ]}
                                  />
                                </Box>
                                {p.evidence_quotes.slice(0, 2).map((q) => (
                                  <Typography
                                    key={q}
                                    variant="body2"
                                    sx={{
                                      mt: 1,
                                      fontStyle: 'italic',
                                      color: 'text.secondary',
                                    }}
                                  >
                                    “{q}”
                                  </Typography>
                                ))}
                              </Paper>
                            ))
                          )}
                        </Stack>
                      )}

                      {tab === 2 && (
                        <Stack spacing={1.25}>
                          {findings.corporate_transactions.length === 0 ? (
                            <Typography color="text.secondary">
                              No corporate transactions found. Try Run live extract.
                            </Typography>
                          ) : (
                            findings.corporate_transactions.map((f, idx) => (
                              <Paper
                                key={f.id || f.candidate_id || `${f.transaction_type}-${idx}`}
                                variant="outlined"
                                sx={{ p: 1.5 }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  flexWrap="wrap"
                                  useFlexGap
                                  mb={0.75}
                                >
                                  <Chip
                                    size="small"
                                    label={
                                      f.origin === 'live_extract'
                                        ? 'Live'
                                        : f.origin === 'review_candidate'
                                          ? 'Scheduled review'
                                          : 'Canonical'
                                    }
                                    color={f.origin === 'canonical' ? 'default' : 'warning'}
                                  />
                                  <Chip size="small" label={titleCase(f.transaction_class)} />
                                  <Chip
                                    size="small"
                                    label={titleCase(f.transaction_type)}
                                    color="primary"
                                    variant="outlined"
                                  />
                                  {f.status && <Chip size="small" label={f.status} />}
                                </Stack>
                                {f.origin !== 'canonical' && f.candidate && f.candidate_id && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                  >
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      startIcon={
                                        candidateStates[f.candidate_id]?.status === 'saving' ? (
                                          <CircularProgress size={14} color="inherit" />
                                        ) : (
                                          <AddRounded />
                                        )
                                      }
                                      disabled={
                                        savingCorporateTransaction ||
                                        extracting ||
                                        candidateStates[f.candidate_id]?.status === 'saving' ||
                                        candidateStates[f.candidate_id]?.status === 'saved' ||
                                        (['partnership', 'corporate_status'].includes(
                                          f.candidate.transaction_class
                                        ) &&
                                          f.candidate.parties.some(
                                            (party) => party.resolution_status !== 'matched'
                                          ))
                                      }
                                      onClick={() => void saveCorporateTransactionCandidate(f.candidate_id!)}
                                    >
                                      {candidateStates[f.candidate_id]?.status === 'saved'
                                        ? 'Saved'
                                        : candidateStates[f.candidate_id]?.status === 'saving'
                                          ? 'Saving…'
                                          : 'Save to Neo4j'}
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                      Pantograph reverifies source evidence before writing.
                                    </Typography>
                                  </Stack>
                                )}
                                {f.announced_date && (
                                  <Typography variant="caption" color="text.secondary">
                                    Announced {f.announced_date}
                                  </Typography>
                                )}
                                {f.summary && (
                                  <Typography variant="body2">{f.summary}</Typography>
                                )}
                                {f.parties?.length > 0 && (
                                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    {f.parties.map((party, partyIdx) => {
                                      const name =
                                        (party.company_name as string) ||
                                        (party.company_id as string) ||
                                        'Party';
                                      const role = party.role
                                        ? ` · ${titleCase(String(party.role))}`
                                        : '';
                                      const status = String(
                                        party.resolution_status || 'unresolved'
                                      );
                                      const confidence = party.match_confidence
                                        ? String(party.match_confidence)
                                        : null;
                                      const chipColor =
                                        status === 'matched'
                                          ? 'success'
                                          : status === 'not_found'
                                            ? 'error'
                                            : status === 'ambiguous' || status === 'needs_review'
                                              ? 'warning'
                                              : 'default';
                                      const chipLabel =
                                        status === 'matched'
                                          ? `SOZO: ${
                                              party.resolved_company_name ||
                                              party.company_id ||
                                              'matched'
                                            }${confidence ? ` (${confidence})` : ''}`
                                          : status === 'not_found'
                                            ? 'Not in SOZO'
                                            : status === 'ambiguous'
                                              ? 'Ambiguous — not linked'
                                              : status === 'needs_review'
                                                ? 'Needs review — not linked'
                                                : 'Unresolved';
                                      return (
                                        <Stack
                                          key={`${name}-${partyIdx}`}
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                          flexWrap="wrap"
                                          useFlexGap
                                        >
                                          <Typography variant="body2">
                                            {name}
                                            {role}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            color={chipColor as any}
                                            label={chipLabel}
                                            variant="outlined"
                                          />
                                        </Stack>
                                      );
                                    })}
                                  </Stack>
                                )}
                                {f.financial_terms?.length > 0 && (
                                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                                    {f.financial_terms.map((term, termIdx) => (
                                      <Typography
                                        key={`${term.term_type || 'term'}-${termIdx}`}
                                        variant="body2"
                                      >
                                        <strong>
                                          {titleCase(String(term.term_type || 'term'))}
                                        </strong>
                                        {term.raw_value_text
                                          ? `: ${String(term.raw_value_text)}`
                                          : ''}
                                        {term.disclosure_status
                                          ? ` (${String(term.disclosure_status)})`
                                          : ''}
                                      </Typography>
                                    ))}
                                  </Stack>
                                )}
                                {(f.candidate?.evidence || f.evidence || []).length
                                  ? (f.candidate?.evidence || f.evidence || []).map((evidence) => (
                                      <Box key={evidence.id} sx={{ mt: 1 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontStyle: 'italic',
                                            color: 'text.secondary',
                                          }}
                                        >
                                          “{evidence.evidence_text}”
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {[
                                            evidence.page_or_slide
                                              ? `Page/slide ${evidence.page_or_slide}`
                                              : null,
                                            evidence.passage_locator,
                                          ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                        </Typography>
                                      </Box>
                                    ))
                                  : f.evidence_quotes.map((quote, evidenceIdx) => (
                                      <Typography
                                        key={`${quote}-${evidenceIdx}`}
                                        variant="body2"
                                        sx={{
                                          mt: 1,
                                          fontStyle: 'italic',
                                          color: 'text.secondary',
                                        }}
                                      >
                                        “{quote}”
                                      </Typography>
                                    ))}
                                {f.candidate_id &&
                                  candidateStates[f.candidate_id]?.status === 'saved' && (
                                    <Alert severity="success" sx={{ mt: 1 }}>
                                      {candidateStates[f.candidate_id]?.message}
                                    </Alert>
                                  )}
                                {f.candidate_id &&
                                  candidateStates[f.candidate_id]?.status === 'error' && (
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                      {candidateStates[f.candidate_id]?.message}
                                    </Alert>
                                  )}
                              </Paper>
                            ))
                          )}
                        </Stack>
                      )}
                    </>
                  )}
                </Stack>
              )}
            </Paper>
          </Stack>
        )}

        {inventory?.has_more && !inventory.company_id && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => {
                void loadMoreDocuments();
              }}
              disabled={loadingMoreDocs}
              startIcon={loadingMoreDocs ? <CircularProgress size={16} /> : undefined}
            >
              {loadingMoreDocs ? 'Loading older documents…' : 'Load older documents'}
            </Button>
          </Box>
        )}
      </Stack>
    </Container>
  );
};

export default DocumentIntelligencePage;
